// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever talks to a server; the AI calls (question generation + per-answer
// feedback) live in brain.ts and send only the setup fields / the one question +
// the user's own answer text.
//
// Data model:
//   session  { id, role, seniority, interview_type, mode, company, focus, brief,
//              created_at, updated_at,
//              question_count, answered_count, themes[] }        (counts computed on read)
//   question { id, session_id, idx, question, hint, answer, feedback_json,
//              themes, answer_seconds, answered_at, created_at }
//   best     { id, question, answer, role, note, created_at, updated_at }

import { invoke } from "./tauri-web";

export type Seniority = "junior" | "mid" | "senior" | "lead";
export type InterviewType = "behavioral" | "technical" | "mixed";
export type PracticeMode = "prep" | "simulate";

export interface Session {
  id: string;
  /** Field / role practiced for (free text, e.g. "frontend engineer"). */
  role: string;
  seniority: Seniority;
  interview_type: InterviewType;
  /** "prep" (coached, untimed) | "simulate" (timed, review at the end). */
  mode: PracticeMode;
  /** Company / organization from the briefing ("" when none given). */
  company: string;
  /** Short " · "-joined focus points extracted from the briefing ("" when none). */
  focus: string;
  /** The raw briefing text the user gave (job posting, notes; "" when none). */
  brief: string;
  /** The AI session report JSON, verbatim as returned ("" until written). Parse with parseReport(). */
  report_json: string;
  created_at: string;
  updated_at: string;
  /** Computed on read. */
  question_count: number;
  answered_count: number;
  /** Distinct feedback themes flagged in this session (fixed vocab, deduped). */
  themes: string[];
}

export interface SessionQuestion {
  id: string;
  session_id: string;
  /** 0-based order within the session. */
  idx: number;
  question: string;
  /** "What a good answer covers" hint, generated with the question. */
  hint: string;
  /** The user's own answer text. "" until answered. */
  answer: string;
  /** Structured feedback JSON exactly as the brain returned it. "" until answered. */
  feedback_json: string;
  /** Comma-joined fixed-vocab feedback themes. "" until answered. */
  themes: string;
  /** Seconds spent answering this question (on-device clock; 0 = not recorded). */
  answer_seconds: number;
  answered_at: string | null;
  created_at: string;
}

export interface NewSession {
  role: string;
  seniority: Seniority;
  interview_type: InterviewType;
  mode?: PracticeMode;
  company?: string;
  focus?: string;
  brief?: string;
  questions: { question: string; hint: string }[];
}

export interface ThemeCount {
  theme: string;
  count: number;
}

/** A kept "best answer" — the user's curated library of their strongest answers. */
export interface BestAnswer {
  id: string;
  question: string;
  answer: string;
  role: string;
  note: string;
  created_at: string;
  updated_at: string;
}

// The structured per-answer feedback (mirrors the server's /feedback response shape).
// Stored verbatim in question.feedback_json; parse with parseFeedback().
export interface Feedback {
  strengths: string[];
  gaps: string[];
  /** One observation about STAR structure (Situation/Task/Action/Result) in the answer. */
  star_note: string;
  /** ONE stronger phrasing of a part of the user's own answer — their facts, better said. */
  better_example: string;
  /** Fixed-vocab tags of what the feedback flagged (for progress-over-time aggregation). */
  themes: string[];
}

/** Parse a stored feedback_json string back into Feedback; null when empty/corrupt. */
export function parseFeedback(raw: string): Feedback | null {
  if (!raw || !raw.trim()) return null;
  try {
    const j = JSON.parse(raw) as Partial<Feedback>;
    return {
      strengths: Array.isArray(j.strengths) ? j.strengths.map(String) : [],
      gaps: Array.isArray(j.gaps) ? j.gaps.map(String) : [],
      star_note: String(j.star_note ?? ""),
      better_example: String(j.better_example ?? ""),
      themes: Array.isArray(j.themes) ? j.themes.map(String) : [],
    };
  } catch {
    return null;
  }
}

// The whole-session report (mirrors the server's /report response shape).
// Stored verbatim in session.report_json; parse with parseReport().
export interface SessionReport {
  /** 2-3 plain sentences on how the session went, grounded in the answers. */
  summary: string;
  /** Patterns that worked across answers (cite question numbers). */
  strengths: string[];
  /** Patterns to fix across answers. */
  gaps: string[];
  /** Concrete practice instructions for the next session. */
  next_steps: string[];
  /** 1-based question numbers most worth answering again — drives the re-practice loop. */
  repractice: number[];
}

/** Parse a stored report_json string back into SessionReport; null when empty/corrupt. */
export function parseReport(raw: string | undefined): SessionReport | null {
  if (!raw || !raw.trim()) return null;
  try {
    const j = JSON.parse(raw) as Partial<SessionReport>;
    const r: SessionReport = {
      summary: String(j.summary ?? "").trim(),
      strengths: Array.isArray(j.strengths) ? j.strengths.map(String) : [],
      gaps: Array.isArray(j.gaps) ? j.gaps.map(String) : [],
      next_steps: Array.isArray(j.next_steps) ? j.next_steps.map(String) : [],
      repractice: Array.isArray(j.repractice)
        ? j.repractice.map((n) => Math.round(Number(n))).filter((n) => Number.isFinite(n) && n >= 1)
        : [],
    };
    if (!r.summary && r.strengths.length === 0 && r.gaps.length === 0) return null;
    return r;
  } catch {
    return null;
  }
}

/** Profile key for the user's pasted resume (on-device; sent only with the calls that use it). */
export const PROFILE_RESUME_KEY = "resume";

export const store = {
  listSessions: (opts?: { limit?: number }) =>
    invoke<Session[]>("list_sessions", { limit: opts?.limit ?? 50000 }),
  getSession: (id: string) => invoke<Session | null>("get_session", { id }),
  createSession: (input: NewSession) => invoke<Session>("create_session", { input }),
  deleteSession: (id: string) => invoke<void>("delete_session", { id }),
  listQuestions: (sessionId: string) =>
    invoke<SessionQuestion[]>("list_questions", { sessionId }),
  recordAnswer: (questionId: string, answer: string, feedback: Feedback, answerSeconds = 0) =>
    invoke<void>("record_answer", {
      questionId,
      answer,
      feedbackJson: JSON.stringify(feedback),
      themes: feedback.themes,
      answerSeconds,
    }),
  /** Record an answer WITHOUT feedback — "answer now, get coaching later" (simulation /
   *  save-and-next). A later coaching call records the same answer again WITH feedback. */
  recordAnswerOnly: (questionId: string, answer: string, answerSeconds = 0) =>
    invoke<void>("record_answer", {
      questionId,
      answer,
      feedbackJson: "",
      themes: [] as string[],
      answerSeconds,
    }),
  /** Store the AI session report JSON on its session (verbatim, like feedback_json). */
  recordReport: (sessionId: string, report: SessionReport) =>
    invoke<void>("record_report", { sessionId, reportJson: JSON.stringify(report) }),
  /** Feedback-theme counts across ALL answered questions (local progress aggregate). */
  themeCounts: () => invoke<ThemeCount[]>("theme_counts"),

  // ── Profile — small on-device key/value store (the pasted resume lives here) ─
  getProfile: (key: string) => invoke<string | null>("get_profile", { key }),
  setProfile: (key: string, value: string) => invoke<void>("set_profile", { key, value }),

  // ── Best answers — the kept library of the user's strongest answers ─────────
  listBestAnswers: () => invoke<BestAnswer[]>("list_best_answers"),
  saveBestAnswer: (question: string, answer: string, role = "", note = "") =>
    invoke<BestAnswer>("save_best_answer", { question, answer, role, note }),
  deleteBestAnswer: (id: string) => invoke<void>("delete_best_answer", { id }),

  // ── Photographed job posting → on-device OCR (image bytes never reach a server) ─
  savePagePhoto: (bytes: number[], ext: string) => invoke<string>("save_page_photo", { bytes, ext }),
  // Best-effort cleanup of a save_page_photo copy after OCR — the file was only the OCR input.
  deletePagePhoto: (path: string) => invoke<void>("delete_page_photo", { path }),
  ocrImage: (path: string) => invoke<string>("ocr_image", { path }),
};

// ── Pure policy / display helpers ──────────────────────────────────────────────

export const SENIORITIES: Seniority[] = ["junior", "mid", "senior", "lead"];
export const SENIORITY_LABELS: Record<Seniority, string> = {
  junior: "Junior",
  mid: "Mid-level",
  senior: "Senior",
  lead: "Lead / Manager",
};

export const INTERVIEW_TYPES: InterviewType[] = ["behavioral", "technical", "mixed"];
export const TYPE_LABELS: Record<InterviewType, string> = {
  behavioral: "Behavioral",
  technical: "Technical",
  mixed: "Mixed",
};

export const PRACTICE_MODES: PracticeMode[] = ["prep", "simulate"];
export const MODE_LABELS: Record<PracticeMode, string> = {
  prep: "Prepare",
  simulate: "Simulate",
};
export const MODE_HINTS: Record<PracticeMode, string> = {
  prep: "Untimed — hints open, coaching after every answer, refine and retry.",
  simulate: "Like the real thing — a countdown per question, no hints, review it all at the end.",
};
/** Simulation-mode per-question countdown (a typical interview answer budget). */
export const SIMULATE_SECONDS = 120;

/** What to work on for each recurring feedback theme — local, deterministic next-steps
 *  (no AI narration of the user's history; plain coaching-vocabulary guidance). */
export const THEME_NEXT_STEPS: Record<string, string> = {
  structure: "Practice the STAR shape: one situation, your task, the action you took, the result.",
  specificity: "Swap general claims for one named project, tool, or moment per answer.",
  impact: "End answers with the outcome — a number, a change, or what happened because of you.",
  brevity: "Aim for 60–90 seconds: one situation, one action, one result — then stop.",
  relevance: "Tie each answer back to the question asked before adding anything extra.",
  ownership: 'Say "I" for the parts you did — keep "we" only for genuinely shared work.',
  examples: "Prepare 3–4 concrete stories you can bend to many questions.",
  confidence: 'Cut hedges ("just", "kind of", "I think maybe") — state what you did plainly.',
};

/** The fixed feedback-theme vocabulary the brain tags with (mirrors the server prompt). */
export const THEME_VOCAB = [
  "structure", "specificity", "impact", "brevity",
  "relevance", "ownership", "examples", "confidence",
] as const;

export const THEME_LABELS: Record<string, string> = {
  structure: "Structure (STAR)",
  specificity: "Specificity",
  impact: "Impact / results",
  brevity: "Brevity",
  relevance: "Relevance",
  ownership: "Ownership (“I” vs “we”)",
  examples: "Concrete examples",
  confidence: "Confident wording",
};

export function themeLabel(theme: string): string {
  return THEME_LABELS[theme] ?? theme;
}

/** Suggested roles for the setup datalist; the user can type anything. */
export const ROLE_SUGGESTIONS = [
  "software engineer", "frontend engineer", "backend engineer", "data analyst",
  "product manager", "project manager", "UX designer", "marketing manager",
  "sales representative", "customer support specialist", "accountant", "nurse",
  "teacher", "operations manager", "HR generalist",
];

/** Normalize a tag — lowercase, trim, collapse internal whitespace. */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Human "x days ago" for an ISO date/datetime, relative to local today. */
export function relativeDays(dateStr: string | null): string {
  if (!dateStr) return "never";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return "never";
  const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - then.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} wk ago`;
  if (days < 365) return `${Math.round(days / 30)} mo ago`;
  return `${Math.round(days / 365)} yr ago`;
}
